"use client";

import {JSX, useEffect, useRef, useState} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import {type KeenSliderInstance, useKeenSlider} from "keen-slider/react";

import {Amenity} from "@/entities/rental";

import {useTranslation} from "@/shared/i18n";
import {CurrencyUtils} from "@/shared/lib";
import {Rating} from "@/shared/ui/data-display/rating";
import {Typography} from "@/shared/ui/data-display/typography/typography";
import {assetPath} from "@/shared/utils/asset-path";

import {type Rental} from "../../types";

import "./rental-card.scss";
import "keen-slider/keen-slider.min.css";

export type AdditionalImage = {
    id: number;
    url: string;
    alt: string;
};

export interface RentalCardProps
    extends Pick<Rental, "id" | "title" | "location" | "image" | "rating" | "persons" | "beds" | "bathrooms" | "pricePerNight" | "itinerary"> {
    link?: string;
    blank?: boolean;
    onCardClick?: (id: string | number) => void;
    onCardHover?: (id: string) => void;
    onCardLeave?: () => void;
    additional_images?: Array<AdditionalImage>;
    /** Show price without cents (rounded). Default: true */
    hideCents?: boolean;
}

export function RentalCard(props: RentalCardProps): JSX.Element {
    const {
        id,
        title,
        location,
        image,
        rating,
        persons,
        beds,
        bathrooms,
        pricePerNight,
        itinerary,
        link,
        blank,
        onCardClick,
        onCardHover,
        onCardLeave,
        additional_images,
        hideCents = true,
    } = props;
    const {t} = useTranslation("rental");

    const hasAdditionalImages = additional_images && additional_images.length > 0;

    const allImages = hasAdditionalImages ? [{id: 0, url: image, alt: title}, ...additional_images] : [];

    const [currentSlide, setCurrentSlide] = useState(0);
    const [sliderInstance, setSliderInstance] = useState<KeenSliderInstance | null>(null);
    const autoSlideIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const [sliderRef] = useKeenSlider(
        {
            slides: {
                perView: 1,
                spacing: 0,
            },
            drag: true,
            loop: hasAdditionalImages && allImages.length > 1,
            mode: "snap",
            initial: 0,
        },
        [
            (slider) => {
                setSliderInstance(slider);
                slider.on("slideChanged", () => {
                    setCurrentSlide(slider.track.details.rel);
                });
            },
        ],
    );

    const startAutoSlide = (): void => {
        if (!hasAdditionalImages || allImages.length <= 1 || !sliderInstance) return;

        autoSlideIntervalRef.current = setInterval(() => {
            sliderInstance.next();
        }, 1500);
    };

    const stopAutoSlide = (): void => {
        if (autoSlideIntervalRef.current) {
            clearInterval(autoSlideIntervalRef.current);
            autoSlideIntervalRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            stopAutoSlide();
        };
    }, []);

    const handleClick = (): void => {
        if (onCardClick) {
            onCardClick(id);
        }
    };

    const handleMouseEnter = (): void => {
        if (onCardHover) {
            onCardHover(String(id));
        }
    };

    const handleMouseLeave = (): void => {
        if (onCardLeave) {
            onCardLeave();
        }
    };

    return (
        <Link
            href={link ?? ""}
            className="rental-card"
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            target={blank ? "_blank" : undefined}
            rel={blank ? "noopener noreferrer" : undefined}
        >
            <div className="rental-card__image-wrapper">
                {hasAdditionalImages ? (
                    <>
                        <div ref={sliderRef} className="keen-slider rental-card__slider" onMouseEnter={startAutoSlide} onMouseLeave={stopAutoSlide}>
                            {allImages.map((img) => (
                                <div key={img.id} className="keen-slider__slide rental-card__image-container">
                                    <SmartImage
                                        quality={100}
                                        src={img.url}
                                        alt={img.alt}
                                        fill
                                        className="rental-card__image"
                                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 420px"
                                    />
                                </div>
                            ))}
                        </div>
                        {allImages.length > 1 && (
                            <div className="rental-card__dots">
                                {allImages.map((_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        className={`rental-card__dot ${currentSlide === index ? "rental-card__dot--active" : ""}`}
                                        aria-label={t("goToSlide", {number: index + 1})}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            sliderInstance?.moveToIdx(index);
                                        }}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                ) : (
                    <div className="rental-card__image-container">
                        <SmartImage
                            src={image}
                            alt={title}
                            quality={100}
                            fill={true}
                            className="rental-card__image"
                            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 420px"
                        />
                    </div>
                )}
            </div>

            <div className="rental-card__content">
                <div className="rental-card__header">
                    <div className="rental-card__title-wrap">
                        <Typography as="h3" size={{desktop: 14, mobile: 12}} weight={500} className="rental-card__title">
                            {title}
                        </Typography>
                        <Typography as="p" size={{desktop: 12, mobile: 12}} weight={400} color="paragraph" className="rental-card__location">
                            {location}
                        </Typography>
                    </div>
                    <Rating value={rating} decimals={2} />
                </div>

                <div className="rental-card__amenities">
                    <Amenity
                        icon={assetPath("/images/icons/property/people.svg")}
                        alt={t("persons")}
                        value={persons}
                        plus={false}
                        text={t("persons")}
                    />
                    <Amenity icon={assetPath("/images/icons/property/bed.svg")} alt={t("bedrooms")} value={beds} text={t("bedrooms")} />
                    <Amenity icon={assetPath("/images/icons/property/bath.svg")} alt={t("bathrooms")} value={bathrooms} text={t("bathrooms")} />
                </div>

                <div className="rental-card__price">
                    {itinerary ? (
                        <>
                            <Typography as="span" size={{desktop: 18, mobile: 12}} weight={600} color="primary">
                                {CurrencyUtils.formatUSD(itinerary.totalPrice, hideCents)}
                            </Typography>
                            <Typography as="span" size={{desktop: 14, mobile: 10}} weight={400} color="paragraph" style={{marginLeft: "2px"}}>
                                {t("forNights", {count: itinerary.nights})}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography as="span" size={{desktop: 18, mobile: 12}} weight={600} color="title">
                                {CurrencyUtils.formatUSD(pricePerNight, hideCents)}
                            </Typography>
                            <Typography
                                as="span"
                                size={{desktop: 14, mobile: 10}}
                                weight={400}
                                color="paragraph"
                                className="rental-card__price-period"
                            >
                                {t("perNight")}
                            </Typography>
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
}
